import {
    useDispatch,
    useEffect,
    useState,
    useNavigate,
    useParams,
    useSelector,
    titleToUrl,
} from '../../../imports/generalImport';
import { Box, Container, Button } from '../../../imports/muiImport';
import { setProductDetails, wipeOutProductDetails } from '../../../store/productInfo/action';
import { setNewDenomination, wipeOutDenomination } from '../../../store/productDetails/action';
import {
    createDenominationData,
    fetchProductById,
    isProductLive,
    validateOutOfStock,
} from '../../../pages/PRODUCTDETAILS/productDetailHook';
import { Helmet } from 'react-helmet-async';
import { CircularProgress } from '@mui/material';
import MobileProductDetailImage from '../../mobileComponents/mobileProductDetails/MobileProductDetailImage';
import MobileProductDetails from '../../mobileComponents/mobileProductDetails/MobileProductDetails';
import MobileGiftCardPurchase from '../../mobileComponents/mobileGiftCardPurchase/MobileGiftCardPurchase';
import ScrollToTop from '../../../components/other/ScrollToTop';
import PageNotFound from '../../../pages/OTHERPAGE/PageNotFound';
import { getUniqueClass, isDiscountedProduct } from '../../../hooks/utilsFn/utilsFn';

function MobileHomeProductDetails() {
    const [currentProductId, setCurrentProductId] = useState(null);
    const [productLoading, setProductLoading] = useState(true);
    const [outOfStock, setOutOfStock] = useState(null);
    const [showRangeDenomination, setShowRangeDenomination] = useState(false);
    const [productFound, setProductFound] = useState(false);

    const ProductDetail = useSelector((state) => state.ProductDetail?.allData);

    let { title: urlTitle, id: urlId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const canonicalUrl = `https://www.99gift.in/card-details/${urlId}/${titleToUrl(urlTitle)}`;

    useEffect(() => {
        setProductLoading(true);
        if (urlId != currentProductId) {
            dispatch(wipeOutProductDetails()); // clear product Details
            dispatch(wipeOutDenomination()); // clear previous denomination
            productload(urlId, urlTitle);
            setCurrentProductId(urlId);
        } else {
            // Redirect to home page
            navigate('/');
        }
    }, [urlId, urlTitle]);

    if (productFound) {
        return <PageNotFound />;
    }

    if (productLoading) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    // minWidth: "100%",
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <CircularProgress />
            </Box>
        );
    } else {
        return (
            <>
                <Helmet>
                    <title>
                        {ProductDetail?.['metaSEO']?.['metaTitle']
                            ? ProductDetail?.['metaSEO']?.['metaTitle']
                            : `Buy ${urlTitle} - 99gift.in`}
                    </title>

                    <meta
                        property="og:title"
                        content={
                            ProductDetail?.['metaSEO']?.['metaOGTitle']
                                ? ProductDetail?.['metaSEO']?.['metaOGTitle']
                                : `Buy ${urlTitle} - 99gift.in`
                        }
                    />

                    <meta
                        name="description"
                        content={
                            ProductDetail?.['metaSEO']?.['metaDescription']
                                ? ProductDetail?.['metaSEO']?.['metaDescription']
                                : `Get instant discounts on ${urlTitle} when you purchase them online through 99gift. Take advantage of great deals on ${urlTitle}, coupons, and promo codes.`
                        }
                    />

                    <meta
                        name="og:description"
                        content={
                            ProductDetail?.['metaSEO']?.['metaOGDescription']
                                ? ProductDetail?.['metaSEO']?.['metaOGDescription']
                                : `Get instant discounts on ${urlTitle} when you purchase them online through 99gift. Take advantage of great deals on ${urlTitle}, coupons, and promo codes.`
                        }
                    />

                    <meta property="og:url" content={canonicalUrl} />
                    <link rel="canonical" href={canonicalUrl} />

                    {ProductDetail?.['metaSchema'] && (
                        <script type="application/ld+json">{ProductDetail?.['metaSchema']}</script>
                    )}
                </Helmet>

                <Container
                    sx={{
                        minHeight: '100vh',
                        backgroundColor: 'white.main',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        flexDirection: 'column',
                        alignItems: 'center',
                        pt: 1,
                        mb: 6,
                        p: 0,
                    }}
                >
                    <ScrollToTop />
                    <MobileProductDetailImage image={ProductDetail?.image} />
                    <MobileProductDetails product={ProductDetail} />

                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: 90,
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 1,
                            bgcolor: 'white.main',
                            // backgroundColor: "transparent",
                            zIndex: 1000,
                        }}
                    >
                        {outOfStock ? (
                            <Button
                                variant="contained"
                                fullWidth
                                className={`${getUniqueClass(ProductDetail?.id, ProductDetail?.title)}`}
                                sx={{
                                    textTransform: 'capitalize',
                                    bgcolor: 'error.main',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    borderRadius: 0,
                                    py: 1.5,
                                    fontSize: '1.425rem',
                                    boxShadow: 0,
                                    '&:hover': {
                                        bgcolor: 'error.dark',
                                    },
                                }}
                            >
                                Currently Out of Stock
                            </Button>
                        ) : (
                            <MobileGiftCardPurchase showRangeDenomination={showRangeDenomination} />
                        )}
                    </Box>
                </Container>
            </>
        );
    }

    async function productload(urlId, urlTitle) {
        // TODO : Start Loading
        let mainData = {};
        const productFetched = await fetchProductById(urlId, urlTitle);
        const validateProductTitleResponse = validateProductTitle(productFetched?.data, urlTitle);
        const { productType, discount } = isDiscountedProduct(
            productFetched?.data?.discount,
            productFetched?.data?.cashback_amount
        );

        if (productFetched?.status && validateProductTitleResponse) {
            mainData = { ...productFetched.data };
            mainData.productType = productType;
            mainData.discount = discount;
            mainData.originalDiscount = discount;
            mainData.isCashbackProduct = productType === 'Cashback';

            mainData.payment_methods = mainData?.payment_methods?.map((item) => {
                return {
                    ...item,
                    showValue: mainData?.originalDiscount - item?.percentage > 0 ? mainData?.originalDiscount - item?.percentage : 0,
                }
            });

            dispatch(setProductDetails(mainData));
            const isProductLiveStatus = isProductLive(productFetched?.data.front);

            if (!isProductLiveStatus) {
                // This Porject is Live
                // Check Current Product Range or Denomination

                const isRange = productFetched?.data.isRange;
                // const apiId = productFetched?.data?.api_id;
                const front = productFetched?.data?.front;
                const denom = productFetched?.data?.denomination;
                const validateResponse = validateOutOfStock(isRange, front, denom);
                const outOfStockStatus = validateResponse.outOfStockStatus;
                const rangeDenominationStatus = validateResponse.rangeDenominationStatus;

                if (outOfStockStatus) {
                    // this product is out of stock
                    setOutOfStock(true);
                    setProductLoading(false);
                    return null;
                }

                if (rangeDenominationStatus) {
                    // this product is Range
                    setOutOfStock(false);
                    setProductLoading(false);
                    setShowRangeDenomination(true);
                    return null;
                }

                if (!outOfStockStatus && !rangeDenominationStatus) {
                    // this product denomination is already
                    setOutOfStock(false);
                    setProductLoading(false);
                    setShowRangeDenomination(false);

                    const newDeno = createDenominationData(productFetched?.data);
                    dispatch(setNewDenomination(newDeno));
                    return null;
                }
            } else {
                setOutOfStock(true);
                setProductLoading(false);
                return null;
            }
        } else {
            setProductFound(true);
        }

        // TODO : End Loading
        setProductLoading(false);
        return null;
    }

    function validateProductTitle(actualData, urlTitle) {
        const userGivenTitle = urlTitle?.toLowerCase();
        const ourProductTitle = actualData?.title?.toLowerCase();
        const ourFinalTitle = titleToUrl(ourProductTitle);

        if (ourFinalTitle === userGivenTitle) {
            setProductFound(false);
            return true;
        } else {
            setProductFound(true);
            return true;
        }
    }
}

export default MobileHomeProductDetails;
